import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Loader2, ExternalLink, Trash2, Save, Edit3 } from 'lucide-react'
import type { LoraDetailDTO } from '@/lib/types'

interface LoraPopupProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  lora?: LoraDetailDTO
  isLoading?: boolean
  onUseOnGeneration?: (loraId: number) => void
  onDelete?: (loraId: number) => Promise<void>
  onUpdateNotes?: (loraId: number, notes: string) => Promise<void>
  onLocalNotesUpdate?: (loraId: number, notes: string) => void
}

export function LoraPopup({
  open,
  onOpenChange,
  lora,
  isLoading = false,
  onUseOnGeneration,
  onDelete,
  onUpdateNotes,
  onLocalNotesUpdate,
}: LoraPopupProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isEditingNotes, setIsEditingNotes] = useState(false)
  const [editedNotes, setEditedNotes] = useState('')
  const [isSavingNotes, setIsSavingNotes] = useState(false)

  const handleDelete = async () => {
    if (!lora || !onDelete) return
    setIsDeleting(true)
    try {
      await onDelete(lora.id)
      setDeleteDialogOpen(false)
      onOpenChange(false)
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsDeleting(false)
    }
  }

  const handleStartEditingNotes = () => {
    if (!lora) return
    setEditedNotes(lora.notes || '')
    setIsEditingNotes(true)
  }

  const handleCancelEditingNotes = () => {
    setIsEditingNotes(false)
    setEditedNotes('')
  }

  const handleSaveNotes = async () => {
    if (!lora || !onUpdateNotes) return
    setIsSavingNotes(true)

    // Optimistically update the local state immediately
    if (onLocalNotesUpdate) {
      onLocalNotesUpdate(lora.id, editedNotes)
    }

    try {
      await onUpdateNotes(lora.id, editedNotes)
      setIsEditingNotes(false)
    } catch (error) {
      // If the backend update fails, we could optionally revert the optimistic update
      // For now, the error handling is done in the parent component
      console.error('Failed to save notes:', error)
    } finally {
      setIsSavingNotes(false)
    }
  }

  // Reset notes editing when lora changes
  React.useEffect(() => {
    if (lora) {
      setIsEditingNotes(false)
      setEditedNotes('')
    }
  }, [lora])

  const handlePrevious = () => {
    if (!lora?.images.length) return
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : lora.images.length - 1))
  }

  const handleNext = () => {
    if (!lora?.images.length) return
    setCurrentImageIndex((prev) => (prev < lora.images.length - 1 ? prev + 1 : 0))
  }

  const currentImage = lora?.images[currentImageIndex]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] h-[90vh] max-h-[90vh] max-w-none sm:max-w-none p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2 border-b">
          <DialogTitle>LoRA Details</DialogTitle>
        </DialogHeader>

        {isLoading || !lora ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex flex-col h-[calc(90vh-8rem)]">
            <div className="flex gap-4 h-full px-6 pb-6">
              {/* Image Carousel - Takes up most of the space */}
              <div className="flex-1 flex flex-col space-y-3 min-h-0">
                {lora.images.length > 0 ? (
                  <>
                    <div className="relative flex-1 flex items-center justify-center bg-black/5 rounded-lg overflow-hidden">
                      {currentImage?.url ? (
                        <img
                          src={currentImage.url}
                          alt={`${lora.display_name} preview ${currentImageIndex + 1}`}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="text-muted-foreground">No image available</div>
                      )}

                      {/* Navigation Arrows */}
                      {lora.images.length > 1 && (
                        <>
                          <button
                            onClick={handlePrevious}
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-3 transition-colors shadow-lg"
                            aria-label="Previous image"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <button
                            onClick={handleNext}
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-3 transition-colors shadow-lg"
                            aria-label="Next image"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </>
                      )}

                      {/* Image Counter */}
                      {lora.images.length > 1 && (
                        <div className="absolute top-4 right-4 bg-black/60 text-white text-sm px-3 py-1.5 rounded-full shadow-lg">
                          {currentImageIndex + 1} / {lora.images.length}
                        </div>
                      )}
                    </div>

                    {/* Thumbnail Strip */}
                    {lora.images.length > 1 && (
                      <ScrollArea className="w-full max-w-full pb-2">
                        <div className="flex w-max gap-2 pr-2">
                          {lora.images.map((image, index) => (
                            <button
                              key={image.id}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`
                                flex-shrink-0 w-20 h-20 rounded border-2 overflow-hidden transition-all
                                ${index === currentImageIndex ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/50'}
                              `}
                            >
                              {image.url ? (
                                <img
                                  src={image.url}
                                  alt={`Thumbnail ${index + 1}`}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="bg-muted flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                                  N/A
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                        <ScrollBar orientation="horizontal" />
                      </ScrollArea>
                    )}
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center bg-muted rounded-lg">
                    <p className="text-muted-foreground">No preview images available</p>
                  </div>
                )}
              </div>

              {/* Details Panel - Fixed width sidebar */}
              <div className="w-80 space-y-4 text-sm overflow-y-auto">
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">{lora.display_name}</h3>
                  <Badge variant="outline">{lora.base_model}</Badge>
                </div>

                {lora.civitai_url && (
                  <div className="border-b pb-4">
                    <h4 className="font-semibold mb-2">CivitAI Link</h4>
                    <Button variant="outline" size="sm" className="w-full" asChild>
                      <a href={lora.civitai_url} target="_blank" rel="noreferrer">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Open on CivitAI
                      </a>
                    </Button>
                  </div>
                )}

                {lora.trigger_words.length > 0 && (
                  <div className="border-b pb-4">
                    <h4 className="font-semibold mb-2">Trigger Words</h4>
                    <div className="flex flex-wrap gap-2">
                      {lora.trigger_words.map((word, idx) => (
                        <Badge key={idx} variant="secondary">
                          {word}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {lora.description && (
                  <div className="border-b pb-4">
                    <h4 className="font-semibold mb-2">Description</h4>
                    <p className="text-muted-foreground text-sm whitespace-pre-wrap leading-relaxed">
                      {lora.description}
                    </p>
                  </div>
                )}

                {/* Notes Section */}
                <div className="border-b pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">Notes</h4>
                    {!isEditingNotes && onUpdateNotes && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleStartEditingNotes}
                        className="h-6 px-2"
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>

                  {isEditingNotes ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editedNotes}
                        onChange={(e) => setEditedNotes(e.target.value)}
                        placeholder="Add your notes about this LoRA..."
                        className="min-h-[100px] text-sm"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveNotes}
                          disabled={isSavingNotes}
                          className="flex-1"
                        >
                          {isSavingNotes ? (
                            <>
                              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="mr-2 h-3 w-3" />
                              Save
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelEditingNotes}
                          disabled={isSavingNotes}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm whitespace-pre-wrap leading-relaxed min-h-[60px]">
                      {lora.notes || (
                        <span className="italic">No notes added yet. Click Edit to add notes about this LoRA.</span>
                      )}
                    </p>
                  )}
                </div>

                <div className="space-y-2 pt-2">
                  {onUseOnGeneration && (
                    <Button
                      className="w-full"
                      onClick={() => {
                        onUseOnGeneration(lora.id)
                        onOpenChange(false)
                      }}
                    >
                      Use on Generation Page
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      className="w-full"
                      variant="destructive"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete LoRA
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete LoRA</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{lora?.display_name}"? This action cannot be undone.
              This will remove the LoRA from your library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}
